/**
 * Channel hues for the launch timeline.
 *
 * Eight fixed slots, assigned in order and never cycled or generated: a ninth
 * channel folds into the shared "other" slot rather than inventing a colour no
 * one can tell from an existing one.
 *
 * The set is the reference categorical order stepped for a dark surface, and it
 * clears every gate against the ink page background (#0a0a36): OKLCH lightness
 * inside the dark band, chroma above the grey floor, worst adjacent pair ΔE 8.4
 * under protanopia and 19.3 in normal vision, all eight at or above 3:1
 * contrast. Re-run the palette validator before changing any value here — the
 * hues that read as "on brand" (the lavanda/violet family) collapse into each
 * other under colour-vision deficiency, which is what the previous set did.
 */
const CHANNEL_SLOTS = [
  '#3987e5', // blue
  '#d95926', // orange
  '#199e70', // aqua
  '#c98500', // yellow
  '#d55181', // magenta
  '#008300', // green
  '#9085e9', // violet
  '#e66767' // red
] as const;

/** Channels past the eighth slot share one recessive grey, and the legend says
 *  so, rather than each being given a hue that fails the separation checks. */
export const OTHER_COLOUR = '#8b8ba7';

export const OTHER_CHANNEL = 'Other';

const key = (channel?: string | null): string => (channel ?? '').trim().toLowerCase();

/**
 * Colours are assigned from the channels actually present, ordered by how much
 * net each carries, so the busiest channels take the most separable slots.
 *
 * The map is built once per dataset and passed around: assigning by a row's
 * position instead would repaint every channel whenever a filter changes the
 * mix, and a reader who learned "Display is blue" would be misled.
 */
export const buildChannelScale = (
  channels: string[]
): { colourOf: (channel?: string | null) => string; named: string[]; hasOther: boolean } => {
  const named = channels.slice(0, CHANNEL_SLOTS.length);
  const assigned = new Map(named.map((channel, index) => [key(channel), CHANNEL_SLOTS[index]]));

  return {
    colourOf: (channel) => assigned.get(key(channel)) ?? OTHER_COLOUR,
    named,
    hasOther: channels.length > CHANNEL_SLOTS.length
  };
};
